<template>
  <div
      :class="`item card-dark ${isTop ? 'top' : ''} ${highlighted && isMounted ? 'highlighted' : ''}`">
    <div class="f item_top">
      <div class="market f">
        <img
            :alt="offer.market_name"
            :src="getImageFromProxy(`${imagesUrl}${offer.market_name}.png`, '50:50')"
            class="wh24 r4 mr8"
            height="24"
            loading="lazy"
            width="24"
        />
        <span>{{ getMarket(offer.market_name)?.market_full_name }}</span>
      </div>
      <div v-if="isTop" class="top">
        <i class="i-fire mr2"></i>
        <span>TOP</span>
      </div>
      <button
          v-else
          v-tooltip="t('copy_link')"
          :aria-label="t('copy_link')"
          class="btn btn-transparent py2 px10 gray"
          @click="copyHighlightedLink(offer.market_name)"
      >
        <svg height="16" width="16">
          <use xlink:href="/icons/sprite.svg#i-copy"></use>
        </svg>
      </button>
    </div>
    <a
        :href="getMarket(offer.market_name)?.trust_pilot_link"
        class="item-rate f fs gap8 bt bb py12"
        rel="nofollow"
        target="_blank"
    >
      <div
          :class="`item-rate_num ${
          getMarket(offer.market_name)?.rating < 2
            ? 'red'
            : getMarket(offer.market_name)?.rating < 4
              ? 'yellow'
              : 'green'
        }`"
      >
        <p>{{ getMarket(offer.market_name)?.rating }}</p>
      </div>
      <div class="item-rate_vote">
        <div class="item-rate_vote__stars">
          <div
              :style="{ width: `${(100 / 5) * getMarket(offer.market_name)?.rating}%` }"
              class="item-rate_vote__stars--full"
          />
        </div>
        <p>
          {{
            getMarket(offer.market_name)?.votes > 999
                ? Number(getMarket(offer.market_name)?.votes / 1000).toFixed(1) + "K"
                : getMarket(offer.market_name)?.votes
          }}
          {{ t("Vote(s)") }}
        </p>
      </div>
    </a>
    <div class="item-rows">
      <div class="item-rows_item f">
        <span>{{ t("Active offers") }}</span>
        <p>{{ offer.offers }}</p>
      </div>

      <div v-if="offer.price" class="item-rows_item f">
        <span>{{ t(offer.offers ? "From" : "last_price") }}</span>
        <UiPrice :price="offer.price" />
      </div>
      <div v-if="!hideLockedPrice && offer.price_locked" class="item-rows_item f">
        <span>{{ t(offer.offers ? "From" : "last_price") }}</span>
        <UiPrice :price="offer.price_locked" locked />
      </div>
      <div v-if="offer.original_price" class="item-rows_item f">
        <span>{{ t("original_price") }}</span>
        <UiPriceWithCurrency
            :currency="offer.original_currency?.toLowerCase()"
            :price="offer.original_price"
            locked
        />
      </div>

      <div v-if="offer.last_update && user?.is_admin" class="item-rows_item f">
        <span>{{ t('price_updated') }}</span>
        <p class="tar">{{ formattedDate }}</p>
      </div>
    </div>
    <div class="item-footer">
      <!--      <a-->
      <!--          :class="`btn ${isTop ? 'btn-violet' : 'btn-gradient'} h52`"-->
      <!--          :href="getMarketLink(offer.market_name)"-->
      <!--          target="_blank"-->
      <!--          @click="sendGtag(offer.market_name)"-->
      <!--      >-->
      <!--        {{ t('View offer') }}-->
      <!--      </a>-->
      <button
          :class="`btn ${isTop ? 'btn-violet' : 'btn-gradient'} h52`"
          @click="handleButtonClick(offer.market_name, offer.price)"
      >
        {{ t("View offer") }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {getImageFromProxy} from "~/utils/get-image-url";
import type {MarketInfo, MarketOffer} from "~/graphql/generated/schema-types";
import {useI18n} from "vue-i18n";
import {ref} from "vue";
import {encode} from "urlencode";
import moment from "moment/moment";
import {useStore} from "~/store";

const props = defineProps<{
  offer: MarketOffer;
  isTop: boolean;
  markets: MarketInfo[];
  name: string;
  highlighted: boolean;
}>();

const {user} = useStore()
const config = useRuntimeConfig()
const imagesUrl = config.public.imagesUrl
const baseUrlV2 = config.public.baseUrlV2
const {t} = useI18n()
const {gtag} = useGtag()
const {locale} = useI18n()

const interval = ref<any | null>(null);
const formattedDate = ref(getFormattedDate(props.offer?.last_update));

const route = useRoute();
const hideLockedPrice = computed(() => {
  return route.query["locked-price"] == "0";
});
const isMounted = ref(false);

const sendGtag = (market: string) => {
  gtag("event", "csmarket-click", {
    event_category: "offers",
    value: market
  });
};

const marketCache: Record<string, MarketInfo | undefined> = {};

const getMarket = (marketName: string): MarketInfo | undefined => {
  if (marketCache.hasOwnProperty(marketName)) {
    return marketCache[marketName];
  }

  const marketInfo = props.markets?.find((market) => market?.market_name === marketName) as
      | MarketInfo
      | undefined;
  marketCache[marketName] = marketInfo;
  return marketInfo;
};

const getMarketLink = (marketName: string, price: number) => {
  return `${baseUrlV2}/redirect-to-market?marketName=${encode(marketName)}&itemName=${encodeURIComponent(props.name)}&gameId=730&price=${price}`;
};

function isValidDate(dateString: string) {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}

const updateFormattedDate = () => {
  formattedDate.value = getFormattedDate(props.offer.last_update);
};

function getFormattedDate(timestamp: string): string {
  const updateDate = moment(isValidDate(timestamp) ? new Date(timestamp) : +timestamp);
  const now = moment();
  const diffSeconds = now.diff(updateDate, "seconds");
  const diffMinutes = now.diff(updateDate, "minutes");
  const diffHours = now.diff(updateDate, "hours");
  const diffDays = now.diff(updateDate, "days");

  const rtf = new Intl.RelativeTimeFormat(locale.value, {numeric: "always"});

  if (diffSeconds < 60) {
    return rtf.format(-diffSeconds, "second");
  } else if (diffMinutes < 60) {
    return rtf.format(-diffMinutes, "minute");
  } else if (diffHours < 24) {
    return rtf.format(-diffHours, "hour");
  } else {
    return rtf.format(-diffDays, "day");
  }
}

const handleButtonClick = (marketName: string, price: number) => {
  sendGtag(marketName);
  const marketLink = getMarketLink(marketName, price);
  window.open(marketLink, "_blank");
};

const copyHighlightedLink = (marketName: string) => {
  const url = `${window.location.href.split('?highlighted')[0]}?highlighted=${marketName}`;
  navigator.clipboard.writeText(url).then(() => {
    EventBus?.$emit("notify", {
      success: true,
      msg: t("notify.copied")
    });
  }).catch(() => {
    EventBus?.$emit("notify", {
      success: false,
      msg: t("notify.error")
    });
  });

};

onMounted(() => {
  isMounted.value = true;
  const updateDate = moment(
      isValidDate(props.offer?.last_update)
          ? new Date(props.offer?.last_update)
          : +(props.offer?.last_update || 0)
  );

  if (moment().diff(updateDate, "minutes") < 1) {
    interval.value = setInterval(updateFormattedDate, 1000);
  }
});

onUnmounted(() => {
  clearInterval(interval.value);
});
</script>

<style lang="scss" scoped>
.item {
  color: var(--surfaces-on-surface-variable);
  font-weight: 600;
  font-size: 1.6rem;
  padding: 1.6rem 2.4rem;
  border: 1px solid transparent;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  z-index: 1;

  &.highlighted {
    border: 1px solid var(--accent-primary);
    animation: pulse-bg 2s forwards;
    animation-delay: 1s;
  }

  &.top {
    border-color: var(--accent-secondary);
  }

  &_top {
    min-height: 3.5rem;

    .top {
      color: var(--accent-on-accent-secondary);
      background: var(--accent-secondary);
      border-radius: 10rem;
      white-space: nowrap;
      font-size: 14px;
      padding: 6px 8px;
    }
  }

  &-footer {
    margin-top: auto;

    .btn {
      text-align: center;
    }
  }

  &-rate {
    margin-top: 1.4rem;

    &_num {
      background: var(--gr-primary-10);
      border-radius: 0.8rem;
      width: 4rem;
      min-width: 4rem;
      height: 4rem;
      font-weight: 800;
      display: flex;
      align-items: center;
      justify-content: center;

      &.red {
        background: linear-gradient(
                101.99deg,
                rgba(255, 141, 78, 0.1) -3.52%,
                rgba(255, 81, 57, 0.1) 89.43%
        );

        p {
          background: linear-gradient(101.99deg, #e28158 -3.52%, #fb3e3e 89.43%);

          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      }

      &.yellow {
        background: linear-gradient(
                101.99deg,
                rgba(156, 249, 112, 0.1) -3.52%,
                rgba(199, 211, 62, 0.1) 89.43%
        );

        p {
          background: linear-gradient(101.99deg, #8ccc04 -3.52%, #d0d33e 89.43%);

          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      }

      &.green {
        background: var(--gr-primary-10);

        p {
          background: var(--gr-primary);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      }
    }

    &_vote {
      position: relative;

      &__stars {
        height: 20px;
        width: 100px;
        position: relative;
        padding: 1px;
        margin-bottom: 0.4rem;

        &:before {
          content: "";
          display: block;
          position: absolute;
          top: 1px;
          left: 1px;
          right: 1px;
          bottom: 1px;
          background: var(--surfaces-high-container);
        }

        &:after {
          content: "";
          display: block;
          position: absolute;
          left: 0;
          top: 0;
          width: inherit;
          height: inherit;
          background-image: url("data:image/svg+xml,%3Csvg width='100' height='21' viewBox='0 0 100 21' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M100 0.936035H0V20.936H100V0.936035ZM17.9877 9.3429C18.0676 8.92366 17.7479 8.42056 17.3482 8.42056L12.7917 7.74977L10.7133 3.38962C10.6333 3.22192 10.5534 3.13807 10.3935 3.05422C9.99383 2.80268 9.5142 2.97037 9.27439 3.38962L7.27593 7.74977L2.71945 8.42056C2.47963 8.42056 2.31975 8.50441 2.23981 8.67211C1.92006 9.00751 1.92006 9.5106 2.23981 9.846L5.51729 13.2L4.7179 17.9794C4.7179 18.1471 4.7179 18.3148 4.79784 18.4825C5.03766 18.9017 5.51729 19.0694 5.91698 18.8178L9.99383 16.5539L14.0707 18.8178C14.1506 18.9017 14.3105 18.9017 14.4704 18.9017H14.6303C15.0299 18.8178 15.3497 18.3986 15.2698 17.8955L14.4704 13.1161L17.7479 9.76215C17.9077 9.6783 17.9877 9.5106 17.9877 9.3429ZM37.9877 9.3429C38.0676 8.92366 37.7479 8.42056 37.3482 8.42056L32.7917 7.74977L30.7133 3.38962C30.6333 3.22192 30.5534 3.13807 30.3935 3.05422C29.9938 2.80268 29.5142 2.97037 29.2744 3.38962L27.2759 7.74977L22.7194 8.42056C22.4796 8.42056 22.3198 8.50441 22.2398 8.67211C21.9201 9.00751 21.9201 9.5106 22.2398 9.846L25.5173 13.2L24.7179 17.9794C24.7179 18.1471 24.7179 18.3148 24.7978 18.4825C25.0377 18.9017 25.5173 19.0694 25.917 18.8178L29.9938 16.5539L34.0707 18.8178C34.1506 18.9017 34.3105 18.9017 34.4704 18.9017H34.6303C35.0299 18.8178 35.3497 18.3986 35.2698 17.8955L34.4704 13.1161L37.7479 9.76215C37.9077 9.6783 37.9877 9.5106 37.9877 9.3429ZM57.3482 8.42056C57.7479 8.42056 58.0676 8.92366 57.9877 9.3429C57.9877 9.5106 57.9077 9.6783 57.7479 9.76215L54.4704 13.1161L55.2698 17.8955C55.3497 18.3986 55.0299 18.8178 54.6303 18.9017H54.4704C54.3105 18.9017 54.1506 18.9017 54.0707 18.8178L49.9938 16.5539L45.917 18.8178C45.5173 19.0694 45.0377 18.9017 44.7978 18.4825C44.7179 18.3148 44.7179 18.1471 44.7179 17.9794L45.5173 13.2L42.2398 9.846C41.9201 9.5106 41.9201 9.00751 42.2398 8.67211C42.3198 8.50441 42.4796 8.42056 42.7194 8.42056L47.2759 7.74977L49.2744 3.38962C49.5142 2.97037 49.9938 2.80268 50.3935 3.05422C50.5534 3.13807 50.6333 3.22192 50.7133 3.38962L52.7917 7.74977L57.3482 8.42056ZM77.9877 9.3429C78.0676 8.92366 77.7478 8.42056 77.3482 8.42056L72.7917 7.74977L70.7133 3.38962C70.6333 3.22192 70.5534 3.13807 70.3935 3.05422C69.9938 2.80268 69.5142 2.97037 69.2744 3.38962L67.2759 7.74977L62.7194 8.42056C62.4796 8.42056 62.3198 8.50441 62.2398 8.67211C61.9201 9.00751 61.9201 9.5106 62.2398 9.846L65.5173 13.2L64.7179 17.9794C64.7179 18.1471 64.7179 18.3148 64.7978 18.4825C65.0377 18.9017 65.5173 19.0694 65.917 18.8178L69.9938 16.5539L74.0707 18.8178C74.1506 18.9017 74.3105 18.9017 74.4704 18.9017H74.6303C75.0299 18.8178 75.3497 18.3986 75.2698 17.8955L74.4704 13.1161L77.7479 9.76215C77.9077 9.6783 77.9877 9.5106 77.9877 9.3429ZM97.3482 8.42056C97.7478 8.42056 98.0676 8.92366 97.9877 9.3429C97.9877 9.5106 97.9077 9.6783 97.7479 9.76215L94.4704 13.1161L95.2698 17.8955C95.3497 18.3986 95.0299 18.8178 94.6303 18.9017H94.4704C94.3105 18.9017 94.1506 18.9017 94.0707 18.8178L89.9938 16.5539L85.917 18.8178C85.5173 19.0694 85.0377 18.9017 84.7978 18.4825C84.7179 18.3148 84.7179 18.1471 84.7179 17.9794L85.5173 13.2L82.2398 9.846C81.9201 9.5106 81.9201 9.00751 82.2398 8.67211C82.3198 8.50441 82.4796 8.42056 82.7194 8.42056L87.2759 7.74977L89.2744 3.38962C89.5142 2.97037 89.9938 2.80268 90.3935 3.05422C90.5534 3.13807 90.6333 3.22192 90.7133 3.38962L92.7917 7.74977L97.3482 8.42056Z' fill='%23161A23'/%3E%3C/svg%3E%0A");
        }

        &--full {
          margin: 1px;
          height: 18px;
          background: var(--yellow);
          width: 0;
          transition: width 0.3s;
          position: relative;
        }
      }

      .i-star-full {
        margin: 0 1px;
      }

      p {
        font-weight: 400;
        font-size: 1.4rem;
      }
    }
  }

  &-rows {
    // padding: 2rem 0;
    padding-bottom: 0.8rem;

    &_item {
      margin: 1.2rem 0;
      gap: 8px;

      & > span {
        font-size: 1.4rem;
      }

      p {
        color: var(--surfaces-on-surface);
        font-weight: 800;
      }
    }
  }

  &-footer {
    button {
      width: 100%;
      margin-top: 1.6rem;
    }
  }
}

.price {
  color: var(--surfaces-on-surface);
  font-weight: 800;
}

@media screen and (max-width: 480px) {
  .item {
    min-width: 28rem;
  }
}

@keyframes pulse-bg {
  0% {
    //opacity: 1;
    transform: scale(1);
  }
  70% {
    //opacity: 0.5;
    transform: scale(1.15);
  }
  100% {
    //opacity: 1;
    transform: scale(1);
  }
}
</style>
